import { Anchor, Badge, SimpleGrid, Text } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import type { JobStatus, UsageGroup, UsageNames, UsageStatus } from "../../api/generated/model";
import { TYPE_NAMES, type TypeName } from "../../platform/jobs/type-names";
import { formatCount } from "../../ui/format";

const dollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fine = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 4,
});

/**
 * A vendor cost the API sends as a decimal string, in dollars to the cent ("$12.35"), or, for one
 * job's cost under a dollar, to four places ("$0.0556"). Rounded for display only: every sum is
 * the server's.
 */
export function formatUsd(usd: string, small = false): string {
  const value = Number(usd);
  return (small && Math.abs(value) < 1 ? fine : dollars).format(value);
}

const utcDay = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" });
const utcShortDay = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const at = (day: string) => new Date(`${day}T00:00:00Z`);

/** A UTC day as the API counts it ("2026-10-28"), in the reader's words. */
export const formatUtcDay = (day: string) => utcDay.format(at(day));

/** A UTC day for a chart's axis, without its year. */
export const formatUtcShortDay = (day: string) => utcShortDay.format(at(day));

/** A row whose key is null: no user (a system start), no brand or no model. */
export const NO_KEY: Record<UsageGroup, string> = {
  user: "No user",
  team: "No team",
  brand: "No brand",
  model: "No model",
  type: "No type",
  day: "No day",
};

/** A job type's name, as the library's ⓘ says it; none for a type no screen names. */
export const typeName = (type: string): string | undefined =>
  (TYPE_NAMES as Readonly<Record<string, TypeName | undefined>>)[type]?.name;

/**
 * A group's key in words: a user's name (or email), a team's or model's name, a type's name, a
 * UTC day. A brand is its own id, and an id the names lack (one the records no longer have) is
 * itself.
 */
export function keyLabel(by: UsageGroup, key: string, names: UsageNames): string {
  switch (by) {
    case "user": {
      const user = names.users[key];
      return user === undefined ? key : (user.name ?? user.email);
    }
    case "team":
      return names.teams[key] ?? key;
    case "model":
      return names.models[key] ?? key;
    case "type":
      return typeName(key) ?? key;
    case "day":
      return formatUtcDay(key);
    case "brand":
      return key;
  }
}

const STATUS: Record<UsageStatus, { label: string; color?: string }> = {
  open: { label: "Open" },
  succeeded: { label: "Succeeded", color: "teal" },
  failed: { label: "Failed", color: "red" },
  expired: { label: "Expired", color: "red" },
  canceled: { label: "Canceled", color: "gray" },
};

/** A usage line's status: open until its job settles, then the status it settled at. */
export function StatusBadge({ status }: { status: UsageStatus }) {
  const { label, color } = STATUS[status];
  return (
    <Badge variant="light" {...(color === undefined ? {} : { color })}>
      {label}
    </Badge>
  );
}

const JOB_STATUS: Record<JobStatus, { label: string; color?: string }> = {
  queued: { label: "Queued" },
  submitting: { label: "Submitting" },
  running: { label: "Running" },
  finalizing: { label: "Finalizing" },
  succeeded: { label: "Succeeded", color: "teal" },
  failed: { label: "Failed", color: "red" },
  expired: { label: "Expired", color: "red" },
  canceled: { label: "Canceled", color: "gray" },
};

/** A job's own status, as its trace reads it. */
export function JobStatusBadge({ status }: { status: JobStatus }) {
  const { label, color } = JOB_STATUS[status];
  return (
    <Badge variant="light" {...(color === undefined ? {} : { color })}>
      {label}
    </Badge>
  );
}

/** A link to a job's trace: each opening is an audited read. */
export function TraceLink(props: { teamId: string; jobId: string; children: ReactNode }) {
  const { teamId, jobId, children } = props;
  return (
    <Anchor
      renderRoot={(anchor) => (
        <Link {...anchor} to="/staff/teams/$teamId/jobs/$jobId" params={{ teamId, jobId }} />
      )}
    >
      {children}
    </Anchor>
  );
}

/** A span of time in a few characters: "0.8 s", "12 s", "2 min 5 s". */
export function formatDuration(ms: number): string {
  if (ms < 60_000) return `${ms < 10_000 ? (ms / 1000).toFixed(1) : Math.round(ms / 1000)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

/** Facts as a description list, a few to a row. */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <SimpleGrid component="dl" cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" verticalSpacing="xs" m={0}>
      {items.map(([label, value]) => (
        <div key={label}>
          <Text component="dt" size="xs" c="var(--cai-color-text-muted)">
            {label}
          </Text>
          <Text component="dd" m={0} style={{ overflowWrap: "anywhere" }}>
            {value}
          </Text>
        </div>
      ))}
    </SimpleGrid>
  );
}

/** A count of jobs that need a look (an incomplete cost, an unpriced SKU), flagged when not 0. */
export function Flagged({ count }: { count: number }) {
  if (count === 0) return <Text c="var(--cai-color-text-muted)">0</Text>;
  return (
    <Text c="var(--cai-color-text-warning)" fw={500}>
      <TriangleAlert size={12} aria-hidden /> {formatCount(count)}
    </Text>
  );
}

/** "unknown", never $0, for a cost no request has told. */
export const Unknown = () => (
  <Text span c="var(--cai-color-text-warning)" fw={500}>
    unknown
  </Text>
);

/** The known part of a cost that lacks some request's. */
export function PartialCost({ jobs }: { jobs?: number }) {
  const title =
    jobs === undefined
      ? "A request's cost is unknown: this is the known part"
      : `${formatCount(jobs)} jobs have a request whose cost is unknown: this is the known part`;
  return (
    <Badge
      variant="light"
      color="orange"
      size="xs"
      leftSection={<TriangleAlert size={10} aria-hidden />}
      title={title}
    >
      partial
    </Badge>
  );
}

/**
 * What one job's requests cost us as far as known: "pending" while it runs with none known yet,
 * "unknown" once settled with none, and the known part flagged "partial" while some is missing.
 */
export function JobCost(props: { usd: string | null; complete: boolean; open: boolean }) {
  const { usd, complete, open } = props;
  if (usd === null) return open ? <Text c="var(--cai-color-text-muted)">pending</Text> : <Unknown />;
  return (
    <>
      <Text span>{formatUsd(usd, true)}</Text> {complete ? null : <PartialCost />}
    </>
  );
}
